import React, { useState, useRef, useCallback, useEffect, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import throttle from 'lodash/throttle';

import { WpRichTextInput } from 'Wpds/components/rich-text';
import { createOrUpdateWorkspacePrivateNoteAction } from 'Shared/actions/workspacePrivateNote';
import {
  editorStateForEditorComponent,
  editorStateToJsonString,
  isEmptyEditor,
  textOfEditorStateFromJson,
} from 'Shared/utils/draftJsHelpers';

type Props = {
  onSetSaving: Function;
  workspaceId: string;
};

function WorkspacePrivateZoneEditor({ onSetSaving, workspaceId }: Props) {
  const dispatch = useDispatch();
  const savingTimerRef = useRef(null);
  const { id, text } = useSelector(state => state.workspacePrivateNote);

  const [editorState, setEditorState] = useState(editorStateForEditorComponent(text));

  const throttledUpdate = useCallback(
    throttle(
      async newText => {
        const newTextJson = editorStateToJsonString(newText);
        if (newTextJson !== text) {
          onSetSaving(true);
          await dispatch(createOrUpdateWorkspacePrivateNoteAction({ id, text: newTextJson, workspaceId }));
          savingTimerRef.current = setTimeout(() => onSetSaving(false), 500);
        }
      },
      1000,
      { leading: false, trailing: true }
    ),
    []
  );

  useEffect(() => {
    return () => {
      if (savingTimerRef?.current) clearTimeout(savingTimerRef.current);
    };
  }, []);

  const handleEditorChange = useCallback(
    edState => {
      const newTextJson = editorStateToJsonString(edState);
      if (
        isEmptyEditor(edState) &&
        textOfEditorStateFromJson(editorStateToJsonString(editorState)).length !== 1 &&
        newTextJson === text
      ) {
        return setEditorState(edState);
      }
      setEditorState(edState);

      return throttledUpdate(edState);
    },
    [editorState, text, throttledUpdate]
  );

  return (
    <WpRichTextInput
      placeholder="Add your notes here"
      value={editorState}
      onChange={handleEditorChange}
      height="100%"
      qaTest="workspace-private-notes-editor"
    />
  );
}

export default memo(WorkspacePrivateZoneEditor);
